import { parseBlocks, parseInline, safeHref, toggleTaskInSource } from './parse'
import { Icon } from '../Icon'

/** Якорь заголовка для оглавления: кириллица сохраняется. */
function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .trim()
    .replace(/\s+/g, '-') || 'section'
}

/** Заголовки верхнего уровня со сквозными уникальными якорями. */
function collectHeadings(blocks) {
  const used = new Map()
  return blocks
    .filter((b) => b.type === 'heading')
    .map((b) => {
      let slug = slugify(b.text)
      const n = used.get(slug) || 0
      used.set(slug, n + 1)
      if (n) slug = `${slug}-${n + 1}`
      return { level: b.level, text: b.text, slug }
    })
}

const PANEL_ICONS = {
  info: 'info',
  note: 'edit',
  warning: 'warning',
  success: 'check',
  error: 'warning',
}

/**
 * Отрисовка Markdown статьи.
 *
 * Разбор даёт узлы, узлы становятся React-элементами — HTML-строки
 * не возникает, поэтому dangerouslySetInnerHTML здесь не нужен
 * и текст статьи не может стать разметкой.
 *
 * resolveImage переводит ссылку на вложение в адрес, который отдаёт
 * сервер: в теле статьи пишут `![схема](attachment:12)`, а ходить
 * нужно на /api/kb/attachments/12 с проверкой прав.
 */
/**
 * onTasksChange — колбэк с новым исходником после клика по чек-листу.
 * Передаёт его редактор (предпросмотр правит textarea); в режиме
 * чтения колбэка нет и квадратики остаются только для чтения:
 * галочка в сохранённой статье — это правка текста, а правка
 * идёт через редактор и ревизии, а не мимо них.
 */
export function Markdown({ text, resolveImage, className = '', onTasksChange }) {
  const blocks = parseBlocks(text)

  // Сквозная нумерация задач за один проход отрисовки: порядок
  // рендера совпадает с порядком строк исходника.
  const taskCounter = { n: 0 }
  const toggleTask = onTasksChange
    ? (idx) => onTasksChange(toggleTaskInSource(text, idx))
    : null

  if (!blocks.length) {
    return <div className={`md md--empty ${className}`}>Пока пусто</div>
  }

  // Оглавление и якоря считаются один раз на документ: [toc] может
  // стоять в любом месте, а якоря заголовков должны совпадать с ним.
  const headings = collectHeadings(blocks)
  const slugByIndex = new Map()
  let h = 0
  blocks.forEach((b, i) => {
    if (b.type === 'heading') slugByIndex.set(i, headings[h++].slug)
  })

  return (
    <div className={`md ${className}`}>
      {blocks.map((b, i) => (
        <Block
          key={i}
          block={b}
          resolveImage={resolveImage}
          headings={headings}
          anchor={slugByIndex.get(i)}
          taskCounter={taskCounter}
          toggleTask={toggleTask}
        />
      ))}
    </div>
  )
}

function Block({ block, resolveImage, headings = [], anchor, taskCounter, toggleTask }) {
  const inline = (t) => <Inline nodes={parseInline(t)} resolveImage={resolveImage} />
  const sub = (b, i) => (
    <Block
      key={i}
      block={b}
      resolveImage={resolveImage}
      taskCounter={taskCounter}
      toggleTask={toggleTask}
    />
  )

  switch (block.type) {
    case 'heading': {
      const Tag = `h${Math.min(block.level + 1, 6)}`
      return (
        <Tag id={anchor} className={`md__h md__h--${block.level}`}>
          {inline(block.text)}
        </Tag>
      )
    }

    case 'panel':
      // Панель как в Confluence: цветная полоса, иконка типа,
      // необязательный заголовок. Цвета — локальные статусные токены.
      return (
        <aside className={`md__panel md__panel--${block.kind}`}>
          <Icon
            name={PANEL_ICONS[block.kind]}
            size={17}
            className="md__panel-icon"
            aria-hidden="true"
          />
          <div className="md__panel-body">
            {block.title && <p className="md__panel-title">{inline(block.title)}</p>}
            {block.blocks.map(sub)}
          </div>
        </aside>
      )

    case 'align':
      // Контейнер выравнивания: текст, заголовки и картинки внутри
      // подчиняются text-align; сам блок остаётся обычным потоком.
      return (
        <div className={`md__align md__align--${block.dir}`}>
          {block.blocks.map(sub)}
        </div>
      )

    case 'expand':
      // Нативный details: разворот работает и без JS — в письме,
      // в печати и в тесте без обработчиков.
      return (
        <details className="md__expand">
          <summary className="md__expand-summary">{inline(block.title)}</summary>
          <div className="md__expand-body">
            {block.blocks.map(sub)}
          </div>
        </details>
      )

    case 'toc':
      if (!headings.length) return null
      return (
        <nav className="md__toc" aria-label="Содержание">
          <p className="md__toc-title">Содержание</p>
          <ul className="md__toc-list">
            {headings.map((hd) => (
              <li key={hd.slug} className={`md__toc-item md__toc-item--${hd.level}`}>
                <a className="md__toc-link" href={`#${hd.slug}`}>{hd.text}</a>
              </li>
            ))}
          </ul>
        </nav>
      )

    case 'paragraph':
      return (
        <p className={block.indent ? `md__p md__p--i${block.indent}` : 'md__p'}>
          {inline(block.text)}
        </p>
      )

    case 'code':
      return (
        <pre className="md__pre">
          <code data-lang={block.lang || undefined}>{block.text}</code>
        </pre>
      )

    case 'quote':
      return (
        <blockquote className="md__quote">
          {block.blocks.map(sub)}
        </blockquote>
      )

    case 'hr':
      return <hr className="md__hr" />

    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul'
      return (
        <Tag className="md__list">
          {block.items.map((item, i) => (
            <li key={i} className={item.task ? 'md__li md__li--task' : 'md__li'}>
              {item.task && (() => {
                const idx = taskCounter ? taskCounter.n++ : 0
                // В редакторе клик переписывает «[ ]» ↔ «[x]» прямо
                // в исходнике; в режиме чтения колбэка нет и квадратик
                // только для чтения — правка статьи идёт через редактор.
                return (
                  <input
                    type="checkbox"
                    className="md__task-box"
                    checked={item.checked}
                    disabled={!toggleTask}
                    onChange={toggleTask ? () => toggleTask(idx) : undefined}
                    readOnly={!toggleTask}
                    aria-label={item.checked ? 'Выполнено' : 'Не выполнено'}
                  />
                )
              })()}
              {inline(item.text)}
              {item.blocks.map(sub)}
            </li>
          ))}
        </Tag>
      )
    }

    case 'table':
      return (
        // Обёртка со скроллом: широкая таблица не должна распирать
        // колонку статьи и ломать сетку экрана.
        <div className="md__table-scroll">
          <table className="md__table">
            <thead>
              <tr>
                {block.header.map((cell, i) => (
                  <th key={i} className={`md__cell--${block.align[i] || 'left'}`}>
                    {inline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} className={`md__cell--${block.align[c] || 'left'}`}>
                      {inline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )

    default:
      return null
  }
}

function Inline({ nodes, resolveImage }) {
  return (
    <>
      {nodes.map((n, i) => {
        switch (n.type) {
          case 'text':
            return n.value
          case 'code':
            return <code key={i} className="md__code">{n.value}</code>
          case 'strong':
            return <strong key={i}><Inline nodes={n.children} resolveImage={resolveImage} /></strong>
          case 'em':
            return <em key={i}><Inline nodes={n.children} resolveImage={resolveImage} /></em>
          case 'strongEm':
            return (
              <strong key={i}><em><Inline nodes={n.children} resolveImage={resolveImage} /></em></strong>
            )
          case 'del':
            return <del key={i}><Inline nodes={n.children} resolveImage={resolveImage} /></del>
          case 'link': {
            const href = safeHref(n.href)
            const children = <Inline nodes={n.children} resolveImage={resolveImage} />
            if (!href) return <span key={i}>{children}</span>
            const external = /^https?:/i.test(href)
            return (
              <a
                key={i}
                className="md__link"
                href={href}
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {children}
              </a>
            )
          }
          case 'image': {
            const src = resolveImage ? resolveImage(n.href) : safeHref(n.href)
            if (!src) return null
            return (
              <img key={i} className="md__img" src={src} alt={n.alt || ''} loading="lazy" />
            )
          }
          default:
            return null
        }
      })}
    </>
  )
}
